import { createLogger } from '@svenskaspelab/nova-core/log';

import { drawApiResponse } from '../../../schema/draw/entry.js';
import { drawForecastApiResponse } from '../../../schema/forecast/entry.js';
import { type DrawApiResponse, type IDrawSubscriptionService, type ISocketService } from '../../domain/entry.js';
import { drawQueryKeys } from '../../lib/entry.js';
import { getQueryClient } from '../../tanstack/entry.js';

const logger = createLogger('poolbetting.draw-subscription-service');

/**
 * Type guard to check if the value is an array (for socket message payloads).
 *
 * @param value - The value to check.
 * @returns True if value is an array, false otherwise.
 */
function isSocketMessageArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/**
 * Draws service to retrieve data from SvS BE.
 *
 * @param socketService Socket service dependency
 * @returns Draws service to retrieve data
 */
export function drawSubscriptionService(socketService: ISocketService): IDrawSubscriptionService {
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const getDrawCache: IDrawSubscriptionService['getDrawCache'] = (product, { drawNumber }) => {
    const queryClient = getQueryClient();

    return queryClient.getQueryData<DrawApiResponse>(drawQueryKeys.draw(product, drawNumber));
  };

  const subscribeDraw: IDrawSubscriptionService['subscribeDraw'] = (product, query, onUpdate) => {
    const handleUpdate = (socketMessage: unknown): void => {
      try {
        if (!isSocketMessageArray(socketMessage)) {
          return;
        }

        const [data] = socketMessage;

        if (!data) {
          return;
        }

        const parsedData = drawApiResponse.parse(data);

        if ('error' in parsedData) {
          return;
        }

        onUpdate(parsedData.data);
      } catch (error) {
        logger.error('Error parsing draw data', { error, product, drawNumber: query.drawNumber });
        return;
      }
    };

    const socketPath = `/draw/1/${product.toLowerCase()}/draws/${query.drawNumber}`;

    socketService.getClient().on(socketPath, handleUpdate, {
      sendInitialUpdate: true,
    });

    const unsubscribe = (): void => {
      socketService.getClient().off(socketPath, handleUpdate);
      logger.debug('Unsubscribed from draw', { socketPath, product, drawNumber: query.drawNumber });
    };
    return unsubscribe;
  };

  const subscribeDrawForecast: IDrawSubscriptionService['subscribeDrawForecast'] = (product, query, onUpdate) => {
    const handleUpdate = (socketMessage: unknown): void => {
      try {
        if (!isSocketMessageArray(socketMessage)) {
          return;
        }

        const [data] = socketMessage;

        if (!data) {
          return;
        }

        const parsedData = drawForecastApiResponse.parse(data);

        if ('error' in parsedData) {
          return;
        }

        onUpdate(parsedData.data);
      } catch (error) {
        logger.error('Error parsing draw forecast data', { error, product, drawNumber: query.drawNumber });
        return;
      }
    };

    const socketPath = `/draw/1/${product.toLowerCase()}/draws/forecast/${query.drawNumber}`;

    socketService.getClient().on(socketPath, handleUpdate, {
      sendInitialUpdate: true,
    });

    const unsubscribe = (): void => {
      socketService.getClient().off(socketPath, handleUpdate);
      logger.debug('Unsubscribed from draw forecast', { socketPath, product, drawNumber: query.drawNumber });
    };

    return unsubscribe;
  };

  return {
    getDrawCache,
    subscribeDraw,
    subscribeDrawForecast,
  };
}
